import { motion } from "framer-motion";
import {
  Building2,
  ShieldCheck,
  FileCheck,
  Landmark,
  Mail,
  MapPin,
} from "lucide-react";
import { InfoCard } from "./components/InfoCard";
import { Button } from "./components/Button";
import styles from "./Company.module.scss";
import Header from "../../Common/Header";
import Footer from "../../Common/Footer";

export default function Company() {
  const services = [
    {
      icon: <ShieldCheck className={styles.serviceIcon} />,
      title: "Strategic Sourcing",
      desc: "Identifying and vetting reliable suppliers to ensure the highest quality materials at competitive prices.",
    },
    {
      icon: <FileCheck className={styles.serviceIcon} />,
      title: "E-Procurement",
      desc: "Digital-first procurement processes that reduce manual overhead and increase transparency.",
    },
    {
      icon: <Building2 className={styles.serviceIcon} />,
      title: "Supply Chain Audit",
      desc: "Comprehensive evaluation of existing supply chains to identify bottlenecks and efficiency gains.",
    },
  ];

  return (
    <>
      <Header />
      <div className={styles.container}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroBgOverlay} />

          <div className={styles.heroContent}>
            <div className={styles.heroInner}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className={styles.heroTitle}>
                  Precision in <br />
                  <span className={styles.heroTitlePrimary}>Procurement</span>
                </h1>
                <p className={styles.heroSubtitle}>
                  JIT E-SUPPLY PRIVATE LIMITED provides world-class supply chain
                  and e-procurement solutions designed for speed, transparency,
                  and reliability.
                </p>
                <div className={styles.heroCTA}>
                  <Button size="lg" className={styles.btnPrimary}>
                    Explore Services
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className={styles.btnOutline}
                  >
                    More About Us
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className={styles.aboutSection}>
          <div className={styles.containerInner}>
            <div className={styles.aboutGrid}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className={styles.aboutAccent}></div>
                <h2 className={styles.aboutTitle}>
                  Reliability in every <br />
                  transaction
                </h2>
                <div className={styles.aboutText}>
                  <p>
                    JIT E-SUPPLY PRIVATE LIMITED is a dedicated supply chain
                    solutions provider, focused on bridging the gap between
                    quality manufacturers and businesses through innovative
                    e-procurement platforms.
                  </p>
                  <p>
                    Our "Just In Time" philosophy ensures that our clients
                    receive materials precisely when needed, minimizing
                    inventory costs and maximizing operational efficiency.
                  </p>
                </div>
              </motion.div>

              <div className={styles.aboutInfo}>
                <div className={styles.infoPrimary}>
                  <h4 className={styles.infoLabel}>Corporate Identification</h4>
                  <p className={styles.infoValue}>U47912TN2026PTC189732</p>
                  <p className={styles.infoSubtext}>
                    Registered with MCA, India
                  </p>
                </div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoCard}>
                    <h5 className={styles.infoSmallLabel}>Type</h5>
                    <p className={styles.infoCardValue}>Private Limited</p>
                  </div>
                  <div className={styles.infoCard}>
                    <h5 className={styles.infoSmallLabel}>Capital</h5>
                    <p className={styles.infoCardValue}>₹ 10,00,000</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Corporate Info */}
        <section id="corporate" className={styles.corporateSection}>
          <div className={styles.containerInner}>
            <div className={styles.corporateGrid}>
              <InfoCard
                icon={<Building2 className="w-8 h-8" />}
                title="Category"
                value="LTD by Shares"
                subValue="Non-Govt Company"
                delay={0.1}
              />
              <InfoCard
                icon={<Landmark className="w-8 h-8" />}
                title="Registration"
                value="ROC - Chennai"
                subValue="Tamil Nadu"
                delay={0.2}
              />
              <InfoCard
                icon={<FileCheck className="w-8 h-8" />}
                title="Status"
                value="Active"
                subValue="Compliant"
                delay={0.3}
              />
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className={styles.servicesSection}>
          <div className={styles.containerInner}>
            <div className={styles.servicesHeader}>
              <h2 className={styles.servicesTitle}>Our Expertise</h2>
              <div className={styles.servicesAccent}></div>
            </div>
            <div className={styles.servicesGrid}>
              {services.map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={styles.serviceCard}
                >
                  <div className={styles.serviceIconWrapper}>
                    {service.icon}
                  </div>
                  <h4 className={styles.serviceTitle}>{service.title}</h4>
                  <p className={styles.serviceDesc}>{service.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section id="vision" className={styles.visionSection}>
          <div className={styles.containerInner}>
            <div className={styles.visionGrid}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className={styles.visionLabel}>Mission Statement</h3>
                <p className={styles.visionText}>
                  To revolutionize the supply chain industry with{" "}
                  <span className={styles.visionHighlight}>transparent</span>{" "}
                  e-procurement solutions.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <h3 className={styles.visionLabel}>Global Vision</h3>
                <p className={styles.visionText}>
                  To be the global benchmark for{" "}
                  <span className={styles.visionHighlight}>digital</span> supply
                  chain excellence worldwide.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Management & Contact */}
        <section id="contact" className={styles.contactSection}>
          <div className={styles.containerInner}>
            <div className={styles.contactGrid}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className={styles.contactTitle}>Leadership</h3>
                <div className={styles.leadershipCard}>
                  <div className={styles.leadershipAvatar}>PR</div>
                  <div>
                    <h4 className={styles.leadershipName}>Pavunraj Rajmohan</h4>
                    <p className={styles.leadershipRole}>Managing Director</p>
                    <div className={styles.leadershipDIN}>DIN: 11551357</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <h3 className={styles.contactTitle}>Contact</h3>
                <div className={styles.contactInfo}>
                  <div className={styles.contactItem}>
                    <MapPin className={styles.contactIcon} />
                    <div>
                      <h5 className={styles.contactItemTitle}>Headquarters</h5>
                      <p className={styles.contactItemText}>
                        12/60, Gandhi Nagar, Thuvakudi,
                        <br />
                        Tiruchirappalli - 620015,
                        <br />
                        Tamil Nadu, India
                      </p>
                    </div>
                  </div>
                  <div className={styles.contactItem}>
                    <Mail className={styles.contactIcon} />
                    <div>
                      <h5 className={styles.contactItemTitle}>
                        Electronic Mail
                      </h5>
                      <p className={styles.contactItemEmail}>
                        contact@jitsupply.in
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}
